import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button } from 'reactstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { layoutModeTypes } from "../../Components/constants/layout";
import logoDark from '../../assets/images/exiaLogo_dark.png';
import logoLight from '../../assets/images/exiaLogo_light.png';
import { googleOAuthService } from '../../services/googleOAuthService';
import { updateAuthorizationFromStorage } from '../../helpers/api_helper';

const GoogleOAuthCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing');
    const [error, setError] = useState('');
    const [userInfo, setUserInfo] = useState(null);

    // Layout state selector
    const selectLayoutState = (state) => state.Layout;
    const layoutData = createSelector(
        selectLayoutState,
        (layout) => ({
            layoutModeType: layout.layoutModeType,
        })
    );
    const { layoutModeType } = useSelector(layoutData);

    useEffect(() => {
        // Prevent duplicate calls
        let isProcessing = false;
        
        const handleCallback = async () => {
            // Prevent duplicate processing
            if (isProcessing) {
                console.log('Google OAuth callback already processing, skipping duplicate call');
                return;
            }
            
            isProcessing = true;
            
            try {
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const errorParam = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');

                console.log('Google OAuth callback received:', { code, state, errorParam, errorDescription });

                // Handle OAuth errors
                if (errorParam) {
                    throw new Error(errorDescription || errorParam);
                }

                // Handle OAuth callback with code and state
                if (code && state) {
                    console.log('Processing Google OAuth callback with code and state');
                    setStatus('processing');
                    
                    try {
                        // Call backend to verify Google OAuth code and generate JWT
                        const result = await googleOAuthService.verifyGoogleCallback(code, state);
                        console.log('Google OAuth verification result:', result);
                        
                        if (result.success) {
                            // Extract tokens and user data (same format as regular login)
                            const { token: access_token, refreshToken: refresh_token, user: userData } = result;
                            
                            console.log('Storing tokens exactly like regular login...');
                            
                            // Create user object with profile photo priority logic
                            const user = {
                                id: userData.id,
                                name: userData.name,
                                email: userData.email,
                                role: userData.role || 'user',
                                company_id: userData.company_id,
                                // Profile photo priority: Google OAuth photo > existing profile photo > default
                                profile_picture_url: userData.profile_photo || userData.profile_picture_url || null,
                                avatar_url: userData.profile_photo || userData.avatar_url || null,
                                // Store the full profile photo URL object for compatibility
                                profile_photo_url_object: {
                                    profile_url: userData.profile_photo || userData.profile_photo_url_object?.profile_url || null,
                                    fullsize_url: userData.profile_photo || userData.profile_photo_url_object?.fullsize_url || null,
                                    profile_photo_fullurl: userData.profile_photo || userData.profile_photo_url_object?.profile_photo_fullurl || null
                                }
                            };
                            
                            console.log('✅ User object created with profile photo priority:', user);
                            
                            // Store tokens in sessionStorage with the SAME keys as regular login
                            sessionStorage.setItem("authToken", access_token);
                            sessionStorage.setItem("refreshToken", refresh_token);
                            sessionStorage.setItem("user", JSON.stringify(user));
                            
                            // Update API client authorization headers
                            updateAuthorizationFromStorage();
                            
                            console.log('✅ Google OAuth tokens stored in sessionStorage with regular login keys');
                            console.log('✅ API client authorization headers updated');
                            
                            // Set user info in component state
                            setUserInfo(user);
                            
                            setStatus('success');
                            
                            // Redirect to home after successful authentication
                            setTimeout(() => {
                                navigate('/home');
                            }, 1500);
                            
                            return;
                        } else {
                            throw new Error(result.message || 'Google OAuth verification failed');
                        }
                    } catch (error) {
                        console.error('Google OAuth verification error:', error);
                        setError(error.message || 'Google OAuth verification failed');
                        setStatus('error');
                    }
                } else {
                    throw new Error('Missing Google OAuth code or state parameters');
                }
            } catch (error) {
                console.error('Google OAuth callback error:', error);
                setError(error.message || 'Google OAuth callback failed');
                setStatus('error');
            } finally {
                isProcessing = false;
            }
        };

        handleCallback();
        
        // Cleanup function
        return () => {
            isProcessing = false;
        };
    }, [searchParams, navigate]);

    // Show loading state
    if (status === 'processing') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.LIGHTMODE ? logoLight : logoDark}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <Spinner color="primary" className="me-2" />
                                                    <span>Processing Google OAuth callback...</span>
                                                </div>
                                                <p className="text-muted">
                                                    Please wait while we complete your Google authentication.
                                                </p>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    // Show error state
    if (status === 'error') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.LIGHTMODE ? logoLight : logoDark}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <div className="avatar-md mx-auto">
                                                        <div className="avatar-title bg-danger-subtle text-danger fs-36 rounded-circle">
                                                            <i className='bx bx-error-circle'></i>
                                                        </div>
                                                    </div>
                                                    <h5 className="mt-3 mb-2">Google OAuth Authentication Failed</h5>
                                                    <p className="text-muted mb-4">{error}</p>
                                                </div>
                                                <div className="d-grid gap-2">
                                                    <Button color="primary" onClick={() => navigate('/login')}>
                                                        Try Again
                                                    </Button>
                                                    <Button color="outline-secondary" onClick={() => navigate('/home')}>
                                                        Back to Home
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    // Show success state
    if (status === 'success') {
        return (
            <React.Fragment>
                <ParticlesAuth>
                    <div className="auth-page-content">
                        <Container>
                            <Row className="justify-content-center">
                                <Col md={8} lg={12} xl={5}>
                                    <Card className="overflow-hidden">
                                        <CardBody className="p-4">
                                            <div className="text-center">
                                                <div className="mb-4">
                                                    <img
                                                        src={layoutModeType === layoutModeTypes.LIGHTMODE ? logoLight : logoDark}
                                                        alt=""
                                                        height="34"
                                                    />
                                                </div>
                                                <div className="py-4">
                                                    <div className="avatar-md mx-auto">
                                                        <div className="avatar-title bg-success-subtle text-success fs-36 rounded-circle">
                                                            <i className='bx bx-check-circle'></i>
                                                        </div>
                                                    </div>
                                                    <h5 className="mt-3 mb-2">Google OAuth Authentication Successful!</h5>
                                                    <p className="text-muted mb-4">
                                                        Welcome back! You have been successfully authenticated with Google.
                                                    </p>
                                                </div>
                                                <div className="d-grid gap-2">
                                                    <Button color="success" onClick={() => navigate('/home')}>
                                                        Continue to Home
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </Row>
                        </Container>
                    </div>
                </ParticlesAuth>
            </React.Fragment>
        );
    }

    return null;
};

export default GoogleOAuthCallback; 